/**
 * HUD: DOM overlay, not in-canvas UI. [P5-hud]
 *
 * Turn indicator, per-seat score/meeples/colour, tiles remaining, the current tile with its live
 * rotation, an action log, and the room code. Responsive from 360px to 2560px via `style.css`'s grid
 * (see the `@media (max-width: 640px)` block for the portrait reflow).
 */

import type { GameState, MeepleKind, Rotation, TileDef } from '@carc/engine';
import { SEAT_COLOURS, type SeatColour } from '@carc/protocol';
import type { SoundBoard } from './audio.js';
import { renderTileTexture } from './tile-texture.js';
import type { SeatSetup } from './session.js';

export const seatColourHex = (colour: SeatColour): string =>
  SEAT_COLOURS.find((c) => c.id === colour)?.hex ?? '#999999';

export interface HudCallbacks {
  readonly onToggleSound: () => void;
  readonly onOpenHelp: () => void;
  readonly onRotate: () => void;
  readonly onSkipMeeple: () => void;
}

export class Hud {
  private readonly turnIndicator: HTMLElement;
  private readonly turnClock: HTMLElement;
  private readonly roomCodeEl: HTMLElement;
  private readonly soundBtn: HTMLButtonElement;
  private readonly seatsPanel: HTMLElement;
  private readonly tileCanvas: HTMLCanvasElement;
  private readonly tileCanvasWrap: HTMLElement;
  private readonly stacksLeftEl: HTMLElement;
  private readonly discardedEl: HTMLElement;
  private readonly instructionEl: HTMLElement;
  private readonly stackChoiceEl: HTMLElement;
  private readonly rotateBtn: HTMLButtonElement;
  private readonly skipBtn: HTMLButtonElement;
  private readonly logList: HTMLUListElement;
  private readonly logEntries: string[] = [];

  constructor(
    container: HTMLElement,
    sounds: SoundBoard,
    /**
     * The room code, or `null` for a local game.
     *
     * A local game has no room on the server at all — it used to be given a randomly generated code
     * anyway, displayed with a Copy-link button that produced a URL nobody could join and a code that
     * could never be rejoined or recovered. Showing nothing is honest; showing a dead code is not.
     */
    private readonly roomCode: string | null,
    callbacks: HudCallbacks,
  ) {
    container.innerHTML = '';
    container.classList.remove('hidden');

    const topbar = document.createElement('div');
    topbar.className = 'hud-topbar panel';

    this.turnIndicator = document.createElement('div');
    this.turnIndicator.className = 'turn-indicator';
    topbar.appendChild(this.turnIndicator);

    // The turn clock. Sits beside the turn indicator because it belongs to the same sentence — whose
    // turn it is, and how long they have left. Hidden entirely when no timer is running, so an untimed
    // game is not haunted by a blank clock. [P6-timer]
    this.turnClock = document.createElement('div');
    this.turnClock.className = 'turn-clock hidden';
    topbar.appendChild(this.turnClock);

    const spacer = document.createElement('div');
    spacer.className = 'spacer';
    topbar.appendChild(spacer);

    const helpBtn = document.createElement('button');
    helpBtn.type = 'button';
    helpBtn.className = 'help-btn';
    helpBtn.textContent = '❓ How to play';
    helpBtn.addEventListener('click', callbacks.onOpenHelp);
    topbar.appendChild(helpBtn);

    this.roomCodeEl = document.createElement('div');
    this.roomCodeEl.className = 'room-code';
    if (roomCode !== null) {
      const codeText = document.createElement('span');
      codeText.textContent = 'Room';
      const code = document.createElement('code');
      code.textContent = roomCode;
      const copyBtn = document.createElement('button');
      copyBtn.type = 'button';
      copyBtn.textContent = 'Copy link';
      copyBtn.setAttribute('aria-label', 'Copy shareable link');
      copyBtn.addEventListener('click', () => this.copyLink());
      this.roomCodeEl.append(codeText, code, copyBtn);
      topbar.appendChild(this.roomCodeEl);
    }

    this.soundBtn = document.createElement('button');
    this.soundBtn.type = 'button';
    this.soundBtn.addEventListener('click', callbacks.onToggleSound);
    topbar.appendChild(this.soundBtn);
    this.setSoundMuted(sounds.muted);

    this.seatsPanel = document.createElement('div');
    this.seatsPanel.className = 'hud-seats panel';

    const tilePanel = document.createElement('div');
    tilePanel.className = 'hud-tile-panel panel';
    this.tileCanvasWrap = document.createElement('div');
    this.tileCanvasWrap.style.transition = 'transform 0.15s ease';
    this.tileCanvas = document.createElement('canvas');
    this.tileCanvas.width = 128;
    this.tileCanvas.height = 128;
    this.tileCanvasWrap.appendChild(this.tileCanvas);
    const info = document.createElement('div');
    info.className = 'hud-tile-info';
    this.stacksLeftEl = document.createElement('div');
    this.stacksLeftEl.className = 'stacks-left';
    this.discardedEl = document.createElement('div');
    this.instructionEl = document.createElement('div');
    this.instructionEl.className = 'hud-instruction';

    this.stackChoiceEl = document.createElement('div');
    this.stackChoiceEl.className = 'hud-actions-row';

    this.rotateBtn = document.createElement('button');
    this.rotateBtn.type = 'button';
    this.rotateBtn.className = 'rotate-btn hidden';
    this.rotateBtn.innerHTML = '⟳ Rotate <kbd>R</kbd>';
    this.rotateBtn.setAttribute('aria-label', 'Rotate tile (shortcut: R, right-click, or two-finger tap)');
    this.rotateBtn.addEventListener('click', callbacks.onRotate);

    this.skipBtn = document.createElement('button');
    this.skipBtn.type = 'button';
    this.skipBtn.className = 'hidden';
    this.skipBtn.textContent = 'Skip meeple';
    this.skipBtn.addEventListener('click', callbacks.onSkipMeeple);

    const actionsRow = document.createElement('div');
    actionsRow.className = 'hud-actions-row';
    actionsRow.append(this.rotateBtn, this.skipBtn);

    info.append(this.stacksLeftEl, this.discardedEl, this.instructionEl, this.stackChoiceEl, actionsRow);
    tilePanel.append(this.tileCanvasWrap, info);

    const logPanel = document.createElement('div');
    logPanel.className = 'hud-log panel';
    this.logList = document.createElement('ul');
    logPanel.appendChild(this.logList);

    container.append(topbar, this.seatsPanel, tilePanel, logPanel);
  }

  /** Anchor for the tutorial's first coach mark — "This is your tile". */
  get tilePreviewElement(): HTMLElement {
    return this.tileCanvasWrap;
  }

  /** Anchor for the tutorial's second coach mark — "Not fitting? Rotate it." */
  get rotateButtonElement(): HTMLElement {
    return this.rotateBtn;
  }

  /** Anchor for the tutorial's fourth coach mark — the meeple-or-skip step. */
  get skipButtonElement(): HTMLElement {
    return this.skipBtn;
  }

  /**
   * Override the turn indicator with a plain message — used in online play to say we are waiting on
   * somebody else's device. `render()` reclaims it on the next state update.
   */
  /**
   * Show the seconds left on this turn, or hide the clock when `null`.
   *
   * Goes urgent under ten seconds — a countdown you have to read carefully is not a countdown.
   */
  setTurnClock(secondsRemaining: number | null): void {
    if (secondsRemaining === null) {
      this.turnClock.classList.add('hidden');
      return;
    }
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    this.turnClock.textContent = `⏱ ${minutes}:${String(seconds).padStart(2, '0')}`;
    this.turnClock.classList.remove('hidden');
    this.turnClock.classList.toggle('turn-clock--urgent', secondsRemaining <= 10);
  }

  setTurnBanner(text: string): void {
    this.turnIndicator.classList.remove('mine');
    this.turnIndicator.textContent = text;
  }

  /** Hides every contextual control and instruction — the neutral state between phases. */
  clearActions(): void {
    this.stackChoiceEl.innerHTML = '';
    this.rotateBtn.classList.add('hidden');
    this.skipBtn.classList.add('hidden');
    this.instructionEl.textContent = '';
  }

  /**
   * A tile is in hand: show the rotate button and the placement instruction as real on-screen controls
   * — never just the `R` shortcut or a right-click, so touch users have a visible route to every action.
   */
  showPlacementControls(): void {
    this.clearActions();
    this.instructionEl.textContent = 'Place your tile — click a highlighted square.';
    this.rotateBtn.classList.remove('hidden');
  }

  showMeepleControls(): void {
    this.clearActions();
    this.instructionEl.textContent = 'Put a meeple on this tile, or skip.';
    this.skipBtn.classList.remove('hidden');
  }

  showStackChoice(stacks: readonly number[], onChoose: (stack: number) => void): void {
    this.clearActions();
    for (const stack of stacks) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = `Draw: stack ${stack + 1}`;
      btn.addEventListener('click', () => onChoose(stack));
      this.stackChoiceEl.appendChild(btn);
    }
  }

  setPreviewRotation(rotation: Rotation): void {
    this.tileCanvasWrap.style.transform = `rotate(${rotation * 90}deg)`;
  }

  setSoundMuted(muted: boolean): void {
    this.soundBtn.textContent = muted ? '🔇 Sound off' : '🔊 Sound on';
    this.soundBtn.setAttribute('aria-pressed', String(!muted));
  }

  private async copyLink(): Promise<void> {
    const url = `${location.origin}${location.pathname}#${this.roomCode}`;
    try {
      await navigator.clipboard.writeText(url);
      this.pushLog(`Link copied: ${url}`, false);
    } catch {
      this.pushLog(`Copy this link: ${url}`, false);
    }
  }

  pushLog(text: string, mine: boolean): void {
    this.logEntries.push(text);
    if (this.logEntries.length > 60) this.logEntries.shift();
    const li = document.createElement('li');
    li.textContent = text;
    if (mine) li.classList.add('mine');
    this.logList.appendChild(li);
    while (this.logList.children.length > 60) this.logList.removeChild(this.logList.firstChild as ChildNode);
    this.logList.scrollTop = this.logList.scrollHeight;
  }

  render(state: GameState, seats: readonly SeatSetup[], tiles: ReadonlyMap<string, TileDef>): void {
    const activeSeat = seats[state.activeSeat];
    this.turnIndicator.classList.add('mine');
    this.turnIndicator.innerHTML = '';
    const dot = document.createElement('span');
    dot.className = 'seat-dot';
    dot.style.background = activeSeat?.colour ?? '#999';
    const label = document.createElement('span');
    label.textContent =
      state.phase === 'game_over'
        ? 'Game over'
        : `${activeSeat?.name ?? `Seat ${state.activeSeat + 1}`}'s turn — ${phaseLabel(state.phase)}`;
    this.turnIndicator.append(dot, label);

    this.seatsPanel.innerHTML = '';
    seats.forEach((seat, i) => {
      const row = document.createElement('div');
      row.className = 'seat-row' + (i === state.activeSeat && state.phase !== 'game_over' ? ' active' : '');
      const dotEl = document.createElement('span');
      dotEl.className = 'seat-dot';
      dotEl.style.background = seat.colour;
      const name = document.createElement('span');
      name.className = 'seat-name';
      name.textContent = seat.name;
      const score = document.createElement('span');
      score.className = 'seat-score';
      score.textContent = String(state.scores[i] ?? 0);
      const meta = supplyTray(state, i, seat.colour);
      row.append(dotEl, name, score, meta);
      this.seatsPanel.appendChild(row);
    });

    if (state.currentTile) {
      const def = tiles.get(state.currentTile.defId);
      if (def) {
        const ctx = this.tileCanvas.getContext('2d');
        const source = renderTileTexture(def, { resolution: 128, variationSeed: state.currentTile.drawIndex });
        ctx?.clearRect(0, 0, 128, 128);
        ctx?.drawImage(source, 0, 0);
      }
      this.tileCanvasWrap.style.visibility = 'visible';
    } else {
      this.tileCanvasWrap.style.visibility = 'hidden';
    }

    const stacksLeft = state.stacks.reduce((sum, s) => sum + s.length, 0);
    this.stacksLeftEl.textContent = `${stacksLeft} tiles left`;
    this.discardedEl.textContent = state.discarded.length > 0 ? `${state.discarded.length} discarded (unplaceable)` : '';
  }
}

/** Plural-aware kind names for the tray's accessible label. */
const KIND_NOUN: Readonly<Record<MeepleKind, [one: string, many: string]>> = {
  follower: ['meeple', 'meeples'],
  big: ['big meeple', 'big meeples'],
  abbot: ['abbot', 'abbots'],
  wagon: ['wagon', 'wagons'],
  pig: ['pig', 'pigs'],
};

const KIND_ORDER: readonly MeepleKind[] = ['follower', 'big', 'abbot', 'wagon', 'pig'];

/**
 * The player's tray, as a row of pips rather than a number in words.
 *
 * Modelled on the fold-out drawer of the physical game: you can see at a glance what you still hold.
 * A FILLED pip is a meeple in your hand; a HOLLOW one is a meeple already out on the board. Showing
 * both means the row's width is constant, so "I am running out" reads as the tray emptying rather
 * than as a number you have to remember the previous value of.
 *
 * Kinds beyond the plain follower get their own shape via a modifier class, so the tray stays correct
 * the moment an expansion adds them without any further work here.
 */
function supplyTray(state: GameState, seat: number, colour: string): HTMLElement {
  const tray = document.createElement('span');
  tray.className = 'seat-tray';

  const placed = new Map<MeepleKind, number>();
  for (const m of state.meeples) {
    if (m.seat !== seat) continue;
    placed.set(m.kind, (placed.get(m.kind) ?? 0) + 1);
  }

  const supply = state.supply[seat] ?? {};
  const kinds = KIND_ORDER.filter((k) => (supply[k] ?? 0) > 0 || (placed.get(k) ?? 0) > 0);
  const spoken: string[] = [];

  for (const kind of kinds) {
    const held = supply[kind] ?? 0;
    const out = placed.get(kind) ?? 0;
    const group = document.createElement('span');
    group.className = `tray-group tray-${kind}`;
    for (let i = 0; i < held + out; i++) {
      const pip = document.createElement('span');
      pip.className = i < held ? 'tray-pip' : 'tray-pip spent';
      pip.style.setProperty('--pip-colour', colour);
      group.appendChild(pip);
    }
    tray.appendChild(group);
    const [one, many] = KIND_NOUN[kind];
    spoken.push(`${held} ${held === 1 ? one : many}`);
  }

  // The count still has to be *readable*, not only visible — screen readers get the words.
  tray.setAttribute('aria-label', spoken.length > 0 ? `${spoken.join(', ')} left` : 'no meeples left');
  tray.title = spoken.join(' · ');
  return tray;
}

function phaseLabel(phase: GameState['phase']): string {
  switch (phase) {
    case 'awaiting_draw':
      return 'draw a tile';
    case 'awaiting_placement':
      return 'place the tile';
    case 'awaiting_meeple':
      return 'place a meeple, or skip';
    case 'game_over':
      return '';
  }
}
